import { CartPricingMode } from "@/__generated__/sdk";
import { PRICING_MODE_LABEL } from "@/lib/pricingMode";

type PricedOffer = {
  price: number;
  cashPrice?: number | null;
  creditPrice?: number | null;
  percentage?: number | null;
  maxBasketCount?: number | null;
  stock?: number | null;
};

export type SellerVariantLike = {
  id: number;
  price: number;
  cashPrice?: number | null;
  creditPrice?: number | null;
  stock?: number | null;
  maxBasketCount?: number | null;
  hasStock?: boolean;
  offer?: PricedOffer | null;
  guarantee?: { id?: number; title?: string | null } | null;
  warehouse?: {
    id?: number;
    title?: string | null;
    cashDisplayName?: string | null;
    creditDisplayName?: string | null;
    shippingOriginLabel?: string | null;
  } | null;
};

export type SellerOffer = {
  /** Unique key for list rendering: `${variantId}-${mode}` */
  key: string;
  variantId: number;
  mode: CartPricingMode;
  /** Seller row title (warehouse display name for the mode, or نقدی / اعتباری) */
  label: string;
  shippingOriginLabel: string | null;
  guaranteeTitle: string | null;
  /** Remaining sellable units for low-stock hint */
  stockLeft: number | null;
  /** List price for this mode */
  price: number;
  /** Discounted price for this mode (only when an active offer exists) */
  offerPrice: number | null;
  /** Discount percent for this mode */
  percentage: number | null;
  /** Source variant for ATC / cart hooks */
  variant: SellerVariantLike;
};

export type SellerOfferFilter = "all" | "cash" | "credit";

function pickOfferPrice(offer: PricedOffer | null | undefined, mode: CartPricingMode, listPrice: number): number | null {
  if (!offer) return null;
  const modePrice = mode === CartPricingMode.Cash ? offer.cashPrice : offer.creditPrice;
  // A null offer mode price falls back to the legacy offer price (same as the backend)
  const price = modePrice ?? offer.price;
  return price != null && price > 0 && price < listPrice ? Number(price) : null;
}

function buildOfferForMode(variant: SellerVariantLike, mode: CartPricingMode, listPrice: number): SellerOffer {
  const warehouse = variant.warehouse;
  const displayName = mode === CartPricingMode.Cash ? warehouse?.cashDisplayName : warehouse?.creditDisplayName;
  const offerPrice = pickOfferPrice(variant.offer, mode, listPrice);
  const percentage = offerPrice ? Math.round(100 - (offerPrice / listPrice) * 100) : null;
  const stockCap = variant.offer?.maxBasketCount ?? variant.maxBasketCount ?? variant.stock ?? null;
  const stockLeft = stockCap != null && stockCap > 0 && stockCap <= 3 ? Number(stockCap) : null;

  return {
    key: `${variant.id}-${mode}`,
    variantId: variant.id,
    mode,
    label: displayName?.trim() || warehouse?.title?.trim() || PRICING_MODE_LABEL[mode],
    shippingOriginLabel: warehouse?.shippingOriginLabel?.trim() || null,
    guaranteeTitle: variant.guarantee?.title?.trim() || null,
    stockLeft,
    price: Number(listPrice),
    offerPrice,
    percentage,
    variant,
  };
}

/**
 * Seller rows for a single variant: one per pricing mode that has a price.
 * Variants without any mode price (legacy data) fall back to a single cash row using `price`.
 */
export function getVariantSellerOffers(variant?: SellerVariantLike | null): SellerOffer[] {
  if (!variant) return [];

  const hasModePrice = variant.cashPrice != null || variant.creditPrice != null;
  const modes: { mode: CartPricingMode; price: number | null | undefined }[] = hasModePrice
    ? [
        { mode: CartPricingMode.Cash, price: variant.cashPrice },
        { mode: CartPricingMode.Credit, price: variant.creditPrice },
      ]
    : [{ mode: CartPricingMode.Cash, price: variant.price }];

  return modes
    .filter((item): item is { mode: CartPricingMode; price: number } => item.price != null && item.price > 0)
    .map(({ mode, price }) => buildOfferForMode(variant, mode, price));
}

/**
 * Marketplace seller list: expand every in-stock matching variant into cash/credit rows,
 * cheapest effective price first within each mode (Technolife-like ordering).
 */
export function getSellerOffersForVariants(variants: SellerVariantLike[]): SellerOffer[] {
  const rows = variants
    .filter((variant) => variant.hasStock !== false)
    .flatMap((variant) => getVariantSellerOffers(variant));

  return rows.sort((a, b) => {
    if (a.mode !== b.mode) {
      return a.mode === CartPricingMode.Cash ? -1 : 1;
    }
    const aPay = a.offerPrice ?? a.price;
    const bPay = b.offerPrice ?? b.price;
    return aPay - bPay;
  });
}

export function filterSellerOffers(offers: SellerOffer[], filter: SellerOfferFilter): SellerOffer[] {
  if (filter === "cash") return offers.filter((o) => o.mode === CartPricingMode.Cash);
  if (filter === "credit") return offers.filter((o) => o.mode === CartPricingMode.Credit);
  return offers;
}

export function effectiveSellerPrice(offer: SellerOffer): number {
  return offer.offerPrice ?? offer.price;
}

function attributeSignature(variant: { attrs?: { name: string; value: string }[] | null }): string {
  return [...(variant.attrs ?? [])]
    .map((attr) => `${attr.name}\u0000${attr.value}`)
    .sort()
    .join("\u0001");
}

/**
 * Offer in `mode` for the same variant, or the cheapest same-attribute sibling.
 * Used when the shopper's cart is locked to the other pricing mode.
 */
export function findCompatibleSellerOffer<T extends SellerVariantLike & { attrs?: { name: string; value: string }[] | null }>(
  variants: T[],
  variantId: number,
  mode: CartPricingMode,
): SellerOffer | null {
  const clicked = variants.find((variant) => variant.id === variantId);
  if (!clicked) return null;

  const own = getVariantSellerOffers(clicked).find((offer) => offer.mode === mode);
  if (own) return own;

  const signature = attributeSignature(clicked);
  const siblings = variants.filter((variant) => variant.id !== clicked.id && attributeSignature(variant) === signature);
  return getSellerOffersForVariants(siblings).find((offer) => offer.mode === mode) ?? null;
}

/** Toman amount taken off the list price. Null when this row has no discount. */
export function sellerDiscountAmount(offer: SellerOffer): number | null {
  if (offer.offerPrice == null) return null;
  const diff = Math.round(offer.price - offer.offerPrice);
  return diff > 0 ? diff : null;
}
